/**
 * Resource Service Base
 * Generic CRUD service factory and types for API resources
 */

import { apiService, ApiResponse, QueryParams } from './api.service';

/**
 * Resource query options type (alias for QueryParams)
 */
export type ResourceQueryOptions = QueryParams;

/**
 * Generic CRUD service factory
 */
export class ResourceService<T = any> {
  constructor(protected endpoint: string) {}

  /**
   * Get all resources
   */
  async getAll(params?: QueryParams): Promise<ApiResponse<T[]>> {
    return apiService.get<T[]>(this.endpoint, params);
  }

  /**
   * Get single resource by ID
   */
  async getById(id: string | number, params?: QueryParams): Promise<ApiResponse<T>> {
    return apiService.get<T>(`${this.endpoint}/${id}`, params);
  }

  /**
   * Create new resource
   */
  async create(data: Partial<T>): Promise<ApiResponse<T>> {
    return apiService.post<T>(this.endpoint, data);
  }

  /**
   * Update existing resource
   */
  async update(id: string | number, data: Partial<T>): Promise<ApiResponse<T>> {
    return apiService.put<T>(`${this.endpoint}/${id}`, data);
  }

  /**
   * Delete resource
   */
  async delete(id: string | number): Promise<ApiResponse<T>> {
    return apiService.delete<T>(`${this.endpoint}/${id}`);
  }

  /**
   * Get with custom filters
   */
  async getFiltered(filters: Record<string, any>, params?: QueryParams): Promise<ApiResponse<T[]>> {
    return apiService.get<T[]>(this.endpoint, {
      ...params,
      filters,
    });
  }

  /**
   * Search resources
   */
  async search(searchTerm: string, field: string = 'name', params?: QueryParams): Promise<ApiResponse<T[]>> {
    return apiService.get<T[]>(this.endpoint, {
      ...params,
      filters: {
        [field]: { $containsi: searchTerm },
      },
    });
  }

  /**
   * Get paginated resources
   */
  async getPaginated(
    page: number = 1,
    pageSize: number = 10,
    params?: QueryParams
  ): Promise<ApiResponse<T[]>> {
    return apiService.get<T[]>(this.endpoint, {
      ...params,
      pagination: { page, pageSize },
    });
  }

  /**
   * Count resources
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const response = await apiService.get<T[]>(this.endpoint, {
      pagination: { page: 1, pageSize: 1 },
      filters,
    });
    return response.meta?.pagination?.total || 0;
  }
}
